import { SupplierPageTab } from '@omnea-app/fe-suppliers/details/supplierPageTab'

export type BaseUserMention = {
  id: string
  createdAt: string | null
  comment: {
    id: string
    requestFormId: string | null
    user: {
      id: string
      firstName: string
      lastName: string
      isArchived: boolean
    }
    mentions: {
      id: string
      userId: string | null
      user: {
        firstName: string
        lastName: string
        email: string
        isOutOfOffice: boolean
      } | null
    }[]
    payload: { text: string }
  }
  requestId: string | null
  request: {
    id: string
    name: string
  } | null
  supplier: {
    id: string
    name: string
  } | null
  agreement: {
    id: string
    name: string
  } | null
  isResolved: boolean
  userId?: string
}

type RequestMention = {
  requestId: string
  request: {
    id: string
    name: string
  }
} & Omit<BaseUserMention, 'request' | 'requestId'>

type SupplierMention = {
  supplier: {
    id: string
    name: string
  }
} & Omit<BaseUserMention, 'supplier'>

type AgreementMention = {
  agreement: {
    id: string
    name: string
  }
} & Omit<BaseUserMention, 'agreement'>

const isRequestMention = (mention: BaseUserMention): mention is RequestMention => {
  return mention.requestId !== null
}

const isSupplierMention = (mention: BaseUserMention): mention is SupplierMention => {
  return mention.supplier !== null
}

const isAgreementMention = (mention: BaseUserMention): mention is AgreementMention => {
  return mention.agreement != null
}

const getSupplierLink = (
  supplierMention: SupplierMention,
  generateSupplierDetailRoute: (params: { supplierId: string; tab?: SupplierPageTab }) => string
) => {
  const baseUrl = document.location.origin
  const supplierId = supplierMention.supplier?.id
  const commentId = supplierMention.comment.id

  return `${baseUrl}${generateSupplierDetailRoute({ supplierId, tab: 'overview' })}#comment-${commentId}`
}

const getRequestLink = (mention: RequestMention) => {
  const requestId = mention.requestId
  const commentId = mention.comment.id
  const requestFormId = mention.comment.requestFormId

  const baseUrl = document.location.origin

  if (requestFormId) {
    return `${baseUrl}/requests/detail/${requestId}/my-tasks/${requestFormId}#comment-${commentId}`
  }
  return `${baseUrl}/requests/detail/${requestId}/overview#comment-${commentId}`
}

const getAgreementLink = (mention: AgreementMention) => {
  const baseUrl = document.location.origin
  const agreementId = mention.agreement.id
  const commentId = mention.comment.id

  return `${baseUrl}/agreements/detail/${agreementId}#comment-${commentId}`
}

export const getFormCommentLink = (
  mention: BaseUserMention,
  generateSupplierDetailRoute: (params: { supplierId: string; tab?: SupplierPageTab }) => string
) => {
  const isSupplierComment = isSupplierMention(mention)
  const isRequestComment = isRequestMention(mention)

  if (isSupplierComment) {
    return getSupplierLink(mention, generateSupplierDetailRoute)
  }

  if (isRequestComment) {
    return getRequestLink(mention)
  }

  if (isAgreementMention(mention)) {
    return getAgreementLink(mention)
  }

  return ''
}

export const getMentionTitle = (mention: BaseUserMention) => {
  const isSupplierComment = isSupplierMention(mention)
  const isRequestComment = isRequestMention(mention)

  if (isSupplierComment) {
    return mention.supplier?.name
  }

  if (isRequestComment) {
    return mention.request?.name
  }

  if (isAgreementMention(mention)) {
    return mention.agreement?.name
  }

  return ''
}
